import { useContext } from 'react';

import { useEvent } from '@mint-lib/hooks';

import { Checkbox, Label, TruncateText } from '../../index.js';
import { CommandItem } from '../../lib/command.jsx';
import {
  RichTooltip,
  RichTooltipContent,
  RichTooltipTrigger,
} from '../../lib/rich-tooltip.jsx';
import ComboboxItem from './ComboboxItem.jsx';
import { ComboboxMultipleContext } from './ComboboxMultipleContext.jsx';

function ComboboxMultipleItem({
  className,
  value,
  labelText,
  disabled,
  disabledMessage,
  ...props
}: React.ComponentProps<typeof CommandItem> & {
  labelText: string;
} & (
    | { disabled: boolean; disabledMessage: string }
    | { disabled?: never; disabledMessage?: never }
  )) {
  const ctx = useContext(ComboboxMultipleContext);

  const selected = !!value && ctx?.value.includes(value);

  const itemId = `combobox-item-${value}-${labelText}`;

  const handleClick = useEvent((e: React.MouseEvent) => {
    e.preventDefault();
    if (!value) return;

    ctx?.onSelect(value);
  });

  const itemContent = (
    <ComboboxItem
      value={value}
      onPointerDown={handleClick}
      disabled={disabled}
      color={ctx?.color}
      className="flex max-w-full"
      {...props}
    >
      <Label
        htmlFor={itemId}
        disabled={disabled}
        className="max-w-[200px] cursor-pointer"
      >
        <Checkbox id={itemId} checked={selected} disabled={disabled} />
        <TruncateText>{labelText}</TruncateText>
      </Label>
    </ComboboxItem>
  );

  if (disabled && disabledMessage) {
    return (
      <RichTooltip key={itemId} openDelay={200}>
        <RichTooltipTrigger asChild>{itemContent}</RichTooltipTrigger>
        <RichTooltipContent side="top" align="center">
          {disabledMessage}
        </RichTooltipContent>
      </RichTooltip>
    );
  }
  return itemContent;
}

export default ComboboxMultipleItem;
